"use client";

import { memo, useCallback, useEffect, useId, useState } from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import {
  useA2UIComponent,
  type A2UIComponentProps,
  type Types,
} from "@a2ui/react";

const CheckIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

export const BeeOSCheckBox = memo(function BeeOSCheckBox({
  node,
  surfaceId,
}: A2UIComponentProps<Types.CheckboxNode>) {
  const { resolveString, resolveBoolean, setValue, getValue } =
    useA2UIComponent(node, surfaceId);
  const props = node.properties;
  const id = useId();
  const label = resolveString(props.label);
  const valuePath = props.value?.path;
  const initialChecked = valuePath
    ? (() => { const v = getValue(valuePath); return v != null ? Boolean(v) : false; })()
    : (resolveBoolean(props.value) ?? false);

  const [checked, setChecked] = useState(initialChecked);

  useEffect(() => {
    if (valuePath) {
      const externalValue = getValue(valuePath);
      if (externalValue != null && Boolean(externalValue) !== checked) {
        setChecked(Boolean(externalValue));
      }
    }
  }, [valuePath, getValue]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (props.value?.literalBoolean !== undefined) {
      setChecked(props.value.literalBoolean);
    }
  }, [props.value?.literalBoolean]);

  const handleChange = useCallback(
    (newValue: boolean | "indeterminate") => {
      const boolValue = newValue === true;
      setChecked(boolValue);
      if (valuePath) setValue(valuePath, boolValue);
    },
    [valuePath, setValue],
  );

  const hostStyle: React.CSSProperties =
    node.weight !== undefined
      ? ({ "--weight": node.weight } as React.CSSProperties)
      : {};

  return (
    <div className="a2ui-checkbox" style={hostStyle}>
      <div className="flex items-center gap-2">
        <CheckboxPrimitive.Root
          id={id}
          checked={checked}
          onCheckedChange={handleChange}
          className="peer h-4 w-4 shrink-0 rounded-[4px] border border-primary bg-background ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground transition-colors"
        >
          <CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
            <CheckIcon />
          </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>
        {label && (
          <label
            htmlFor={id}
            className="text-sm font-medium leading-none text-foreground cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            {label}
          </label>
        )}
      </div>
    </div>
  );
});
